// NewProjectPage — minimale intake: klant + adres + pakket.
// Submit → POST /api/projects → navigate naar wizard.

import React, { useState, useRef } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../components/AppLayout.jsx';
import { api, endpoints } from '../api/client.js';

const PACKAGES = [
    { value: 'Brons', labelKey: 'project.new.package.bronze' },
    { value: 'Zilver', labelKey: 'project.new.package.silver' },
    { value: 'Goud', labelKey: 'project.new.package.gold' },
];

function describeApiError(err) {
    if (!err?.statusCode) { return 'Geen verbinding met de server. Probeer het opnieuw.'; }
    if (err.statusCode === 400) { return 'De ingevulde gegevens zijn niet geldig. Controleer klantnaam en adres.'; }
    return 'Project aanmaken is mislukt. Probeer het opnieuw.';
}

export default function NewProjectPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [clientName, setClientName] = useState('');
    const [address, setAddress] = useState('');
    const [pkg, setPkg] = useState('Zilver');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [coverMenuAnchor, setCoverMenuAnchor] = useState(null);
    const cameraRef = useRef(null);
    const libraryRef = useRef(null);

    const handlePickCover = (ev) => {
        const f = ev.target.files?.[0];
        if (!f) return;
        setCoverFile(f);
        const reader = new FileReader();
        reader.onload = () => setCoverPreview(reader.result);
        reader.readAsDataURL(f);
    };

    const handleRemoveCover = () => {
        setCoverFile(null);
        setCoverPreview(null);
        if (cameraRef.current) cameraRef.current.value = '';
        if (libraryRef.current) libraryRef.current.value = '';
    };

    const openCoverMenu = (ev) => setCoverMenuAnchor(ev.currentTarget);
    const closeCoverMenu = () => setCoverMenuAnchor(null);
    const triggerCoverCamera = () => { closeCoverMenu(); cameraRef.current?.click(); };
    const triggerCoverLibrary = () => { closeCoverMenu(); libraryRef.current?.click(); };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        setError(null);
        if (clientName.trim().length < 2 || address.trim().length < 2) {
            setError('Vul een klantnaam en een installatieadres van minimaal 2 tekens in.');
            return;
        }
        setLoading(true);
        try {
            const res = await api.post(endpoints.projects, {
                customer_name: clientName,
                install_address: address,
                metadata: { package: pkg },
            });
            const id = res?.id || res?.project?.id;
            if (!id) { throw new Error('Backend gaf geen project-id terug'); }
            if (coverFile) {
                try {
                    const fd = new FormData();
                    fd.append('photos', coverFile);
                    fd.append('item_id', '__cover__');
                    await api.postMultipart(`/api/projects/${id}/photos`, fd);
                } catch (uploadErr) {
                    console.warn('Projectfoto upload faalde (project is wel aangemaakt)', uploadErr);
                }
            }
            navigate(`/projects/${id}/wizard`, { replace: true });
        } catch (err) {
            setError(describeApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppLayout>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{ mb: 2 }}
            >
                {t('common.back')}
            </Button>

            <Typography variant="h1" sx={{ mb: 3 }}>{t('project.new.title')}</Typography>

            <Card variant="outlined" sx={{ maxWidth: 640 }}>
                <CardContent sx={{ p: 3 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <form onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2.5}>
                            <TextField
                                label={t('project.new.clientName')}
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                                required
                                autoFocus
                            />
                            <TextField
                                label={t('project.new.address')}
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                required
                                multiline
                                minRows={2}
                            />
                            <TextField
                                label={t('project.new.package')}
                                value={pkg}
                                onChange={(e) => setPkg(e.target.value)}
                                select
                                required
                            >
                                {PACKAGES.map(p => (
                                    <MenuItem key={p.value} value={p.value}>
                                        {t(p.labelKey)}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Projectfoto <span style={{ color: '#888', fontWeight: 400 }}>(optioneel)</span>
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    {coverPreview ? (
                                        <Box sx={{ position: 'relative' }}>
                                            <Avatar
                                                variant="rounded"
                                                src={coverPreview}
                                                sx={{ width: 96, height: 96 }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={handleRemoveCover}
                                                sx={{
                                                    position: 'absolute', top: -8, right: -8,
                                                    bgcolor: 'background.paper', boxShadow: 1,
                                                    '&:hover': { bgcolor: 'grey.200' },
                                                }}
                                                aria-label="Projectfoto verwijderen"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <>
                                            <Button
                                                variant="outlined"
                                                startIcon={<AddAPhotoIcon />}
                                                onClick={openCoverMenu}
                                                sx={{ height: 56 }}
                                            >
                                                Foto toevoegen
                                            </Button>
                                            <Menu
                                                anchorEl={coverMenuAnchor}
                                                open={Boolean(coverMenuAnchor)}
                                                onClose={closeCoverMenu}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                            >
                                                <MenuItem onClick={triggerCoverCamera}>
                                                    <ListItemIcon><CameraAltIcon fontSize="small" /></ListItemIcon>
                                                    Foto maken
                                                </MenuItem>
                                                <MenuItem onClick={triggerCoverLibrary}>
                                                    <ListItemIcon><PhotoLibraryIcon fontSize="small" /></ListItemIcon>
                                                    Uit bibliotheek
                                                </MenuItem>
                                            </Menu>
                                            <input
                                                ref={cameraRef}
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                hidden
                                                onChange={handlePickCover}
                                            />
                                            <input
                                                ref={libraryRef}
                                                type="file"
                                                accept="image/*"
                                                hidden
                                                onChange={handlePickCover}
                                            />
                                        </>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        Dient als illustratie in het projectoverzicht.
                                    </Typography>
                                </Stack>
                            </Box>

                            <Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={loading || !clientName.trim() || !address.trim()}
                                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                                >
                                    {t('project.new.submit')}
                                </Button>
                            </Box>
                        </Stack>
                    </form>
                </CardContent>
            </Card>
        </AppLayout>
    );
}
